/** Administración: catálogos, partidas y estado del almacenamiento. */

import { useEffect, useMemo, useState } from 'react';
import { Database, FlaskConical, Loader2, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Boton, Campo, Chip, Tarjeta, TarjetaTitulo, Vacio } from '@/components/ui';
import { useCatalogos } from '@/hooks/useCatalogos';
import { useAuth } from '@/hooks/useAuth';
import { modoLocal } from '@/db/repositorio';
import { borrarEjemplo, cargarEjemplo, contarEjemplo } from '@/db/ejemplo';
import { borrarTodasLasGuias } from '@/db/repositorio';
import { useGuias } from '@/hooks/useGuias';
import { uf } from '@/domain/formato';
import ImportarExcel from './ImportarExcel';
import Usuarios from './Usuarios';
import Contrato from './Contrato';
import { useDialogo } from '@/components/ui/dialogo';
import { cn } from '@/lib/cn';

type Pestana = 'partidas' | 'productos' | 'clientes' | 'destinos' | 'equipos' | 'centros';

const PESTANAS: { id: Pestana; texto: string }[] = [
  { id: 'partidas', texto: 'Partidas del contrato' },
  { id: 'productos', texto: 'Productos' },
  { id: 'clientes', texto: 'Clientes' },
  { id: 'destinos', texto: 'Destinos' },
  { id: 'equipos', texto: 'Equipos' },
  { id: 'centros', texto: 'Centros de costo' },
];

export default function Administracion() {
  const { catalogos, recargar } = useCatalogos();
  const { puede } = useAuth();
  const { guias } = useGuias();
  const [pestana, setPestana] = useState<Pestana>('partidas');
  const [q, setQ] = useState('');
  const [ejemplos, setEjemplos] = useState(0);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const { confirmar, pedirTexto } = useDialogo();

  useEffect(() => {
    void contarEjemplo().then(setEjemplos);
  }, []);

  async function generarEjemplo() {
    setTrabajando(true);
    setAviso(null);
    try {
      const r = await cargarEjemplo();
      setAviso(
        `Se cargaron ${r.guias} guías de los últimos 60 días, ${r.trabajadores} trabajadores y ${r.jornadas} jornadas.`,
      );
      setEjemplos(await contarEjemplo());
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudieron cargar los datos de ejemplo.');
    } finally {
      setTrabajando(false);
    }
  }

  async function quitarEjemplo() {
    const si = await confirmar({
      titulo: 'Borrar los datos de ejemplo',
      mensaje:
        'Se eliminan las guías y jornadas generadas para la demostración. ' +
        'Las guías que cargaste a mano y los catálogos no se tocan.',
      aceptar: 'Borrar',
      tono: 'peligro',
    });
    if (!si) return;
    setTrabajando(true);
    const n = await borrarEjemplo();
    setAviso(`Se borraron ${n} registros de ejemplo.`);
    setEjemplos(await contarEjemplo());
    setTrabajando(false);
  }

  async function vaciarGuias() {
    const escrito = await pedirTexto({
      titulo: `Borrar las ${guias.length.toLocaleString('es-CL')} guías`,
      mensaje:
        'Se eliminan todas las guías de despacho, en todos los dispositivos. ' +
        'Los catálogos, las partidas del contrato y los usuarios no se tocan. ' +
        'No hay forma de deshacerlo: la única vuelta atrás es volver a importar el libro.',
      etiqueta: 'Escribe BORRAR para confirmar',
      marcador: 'BORRAR',
      aceptar: 'Borrar todo',
      tono: 'peligro',
    });
    if (escrito?.trim().toUpperCase() !== 'BORRAR') return;

    setTrabajando(true);
    setAviso(null);
    try {
      const n = await borrarTodasLasGuias();
      setAviso(`Se borraron ${n.toLocaleString('es-CL')} guías. Ya puedes importar el libro.`);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudieron borrar las guías.');
    } finally {
      setTrabajando(false);
    }
  }

  const filtro = q.trim().toLowerCase();
  const coincide = (...campos: (string | null | undefined)[]) =>
    !filtro || campos.filter(Boolean).some((c) => String(c).toLowerCase().includes(filtro));

  const sinClasificar = useMemo(
    () => catalogos.productos.filter((p) => !p.parseOk).length,
    [catalogos.productos],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Administración</h1>
          <p className="text-xs text-texto-mute">Catálogos y partidas del contrato</p>
        </div>
        <Boton
          variante="secundario"
          icono={<RefreshCw size={15} />}
          onClick={() => void recargar()}
        >
          Recargar
        </Boton>
      </div>

      <Tarjeta>
        <TarjetaTitulo
          accion={
            <Chip nivel={modoLocal ? 'AMARILLO' : 'VERDE'}>
              <Database size={12} />
              {modoLocal ? 'Almacenamiento local' : 'Firebase conectado'}
            </Chip>
          }
        >
          Origen de datos
        </TarjetaTitulo>
        <div className="p-4 text-xs text-texto-mute">
          {modoLocal ? (
            <>
              Los datos se guardan en este dispositivo. Para sincronizar entre teléfono, tablet y
              computador hay que crear un proyecto en Firebase y completar las variables del archivo{' '}
              <code className="rounded bg-petroleo-800 px-1 py-0.5 font-mono">.env</code>. Las
              instrucciones están en el README.
            </>
          ) : (
            <>
              Conectado a Firestore con caché offline. Lo que cargues sin señal se sincroniza solo
              al recuperar conexión.
            </>
          )}
        </div>
      </Tarjeta>

      {puede('administrar') && <Usuarios />}

      {puede('contrato') && <Contrato />}

      <ImportarExcel />

      {puede('administrar') && (
        <Tarjeta>
          <TarjetaTitulo
            accion={<Chip nivel="NEUTRO">{guias.length.toLocaleString('es-CL')} guías</Chip>}
          >
            Empezar de cero
          </TarjetaTitulo>
          <div className="space-y-3 p-4">
            <p className="text-xs leading-relaxed text-texto-mute">
              Borra todas las guías para volver a importarlas desde el libro. Sirve cuando la carga
              anterior quedó mal o cambió el archivo de origen. Los catálogos, las partidas del
              contrato y los usuarios quedan como están.
            </p>
            {guias.length === 0 ? (
              // Un botón apagado sin explicación se lee como una falla.
              <p className="text-xs text-texto-mute">
                No hay guías cargadas, así que no hay nada que borrar.
              </p>
            ) : (
              <Boton
                variante="peligro"
                icono={<Trash2 size={15} />}
                onClick={() => void vaciarGuias()}
                disabled={trabajando}
              >
                {trabajando
                  ? 'Borrando…'
                  : `Borrar las ${guias.length.toLocaleString('es-CL')} guías`}
              </Boton>
            )}
          </div>
        </Tarjeta>
      )}

      <Tarjeta>
        <TarjetaTitulo
          accion={
            ejemplos > 0 ? <Chip nivel="AMARILLO">{ejemplos} registros de ejemplo</Chip> : undefined
          }
        >
          Datos de ejemplo
        </TarjetaTitulo>
        <div className="space-y-3 p-4">
          <p className="text-xs text-texto-mute">
            Genera guías de los últimos 60 días y jornadas de un mes, con la mezcla real de
            productos, clientes y plantas, para ver el resumen, los gráficos y el estado de pago
            funcionando antes de cargar datos de verdad. Quedan marcados aparte, así que se pueden
            borrar sin tocar lo que cargues a mano.
          </p>
          <div className="flex flex-wrap gap-2">
            <Boton
              variante="secundario"
              icono={
                trabajando ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <FlaskConical size={15} />
                )
              }
              onClick={() => void generarEjemplo()}
              disabled={trabajando}
            >
              Cargar datos de ejemplo
            </Boton>
            {ejemplos > 0 && (
              <Boton
                variante="peligro"
                icono={<Trash2 size={15} />}
                onClick={() => void quitarEjemplo()}
                disabled={trabajando}
              >
                Borrar los de ejemplo
              </Boton>
            )}
          </div>
          {aviso && <p className="text-xs text-fluor-verde">{aviso}</p>}
        </div>
      </Tarjeta>

      {sinClasificar > 0 && (
        <Tarjeta>
          <TarjetaTitulo>Productos por clasificar</TarjetaTitulo>
          <div className="flex flex-wrap gap-1.5 p-4">
            {catalogos.productos
              .filter((p) => !p.parseOk)
              .map((p) => (
                <Chip key={p.id} nivel="AMARILLO">
                  {p.codigo}
                </Chip>
              ))}
          </div>
          <p className="px-4 pb-4 text-xs text-texto-mute">
            Estos códigos no siguen la nomenclatura estándar, así que no se les aplican recargos
            automáticos. Hay que asignarles partida a mano en cada guía.
          </p>
        </Tarjeta>
      )}

      <Tarjeta>
        <div className="flex flex-wrap gap-1 border-b border-linea p-2">
          {PESTANAS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPestana(p.id)}
              className={cn(
                'rounded-control px-3 py-1.5 text-xs font-medium transition-colors',
                pestana === p.id
                  ? 'bg-petroleo-600 text-texto'
                  : 'text-texto-mute hover:bg-white/5 hover:text-texto',
              )}
            >
              {p.texto}
            </button>
          ))}
        </div>

        <div className="p-4">
          <Campo
            placeholder="Buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sufijo={<Search size={14} />}
          />
        </div>

        <div className="max-h-[32rem] overflow-y-auto">
          {pestana === 'partidas' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Descripción</th>
                  <th className="px-3 py-2 font-medium">Unidad</th>
                  <th className="px-3 py-2 text-right font-medium">Base UF</th>
                  <th className="px-3 py-2 text-right font-medium">Vigente UF</th>
                </tr>
              </thead>
              <tbody>
                {catalogos.items
                  .filter((i) => coincide(i.codigo, i.descripcion))
                  .map((i) => (
                    <tr key={i.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5 font-mono tabular-nums">{i.codigo}</td>
                      <td className="max-w-[22rem] truncate px-3 py-1.5">{i.descripcion}</td>
                      <td className="px-3 py-1.5 text-texto-mute">{i.unidad}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-texto-mute">
                        {uf(i.precioBaseUf)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {uf(i.precioUf)}
                        {!i.aplicaReajuste && (
                          <span className="ml-1 text-[10px] text-texto-mute">sin reajuste</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {pestana === 'productos' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Familia</th>
                  <th className="px-3 py-2 font-medium">Grado</th>
                  <th className="px-3 py-2 text-right font-medium">MPa</th>
                  <th className="px-3 py-2 text-right font-medium">Cono</th>
                  <th className="px-3 py-2 font-medium">Extras</th>
                  <th className="px-3 py-2 font-medium">Alias</th>
                </tr>
              </thead>
              <tbody>
                {catalogos.productos
                  .filter((p) => coincide(p.codigo, ...p.alias))
                  .map((p) => (
                    <tr key={p.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5 font-mono">
                        {p.codigo}
                        {!p.parseOk && <span className="ml-1.5 text-fluor-amarillo">•</span>}
                      </td>
                      <td className="px-3 py-1.5 text-texto-mute">{p.familia}</td>
                      <td className="px-3 py-1.5">{p.grado ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {p.mpa ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                        {p.cono ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-fluor-amarillo">
                        {p.extras.join(' ') || '—'}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-1.5 text-texto-mute">
                        {p.alias.join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {(['clientes', 'destinos', 'equipos', 'centros'] as const).includes(
            pestana as 'clientes',
          ) && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Alias agrupados</th>
                </tr>
              </thead>
              <tbody>
                {(pestana === 'clientes'
                  ? catalogos.clientes
                  : pestana === 'destinos'
                    ? catalogos.destinos
                    : pestana === 'equipos'
                      ? catalogos.equipos
                      : catalogos.centrosCosto
                )
                  .filter((c) => coincide(c.nombre, ...c.alias))
                  .map((c) => (
                    <tr key={c.id} className="border-t border-linea/40">
                      <td className="px-4 py-1.5">{c.nombre}</td>
                      <td className="px-3 py-1.5 text-texto-mute">
                        {c.alias.length ? c.alias.join(' · ') : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {catalogos.items.length === 0 && <Vacio titulo="Sin datos" />}
        </div>
      </Tarjeta>
    </div>
  );
}
